import '@widesk-ui/components/styles/DevToolbar.scss';

import Button                  from '@widesk-ui/components/Button';
import Tooltip                 from '@widesk-ui/components/Tooltip';
import React                   from 'react';
import View                    from '@widesk-ui/components/View';
import AntdInput               from 'antd/lib/input';
import _groupBy                from 'lodash/groupBy';
import _minBy                  from 'lodash/minBy';
import _maxBy                  from 'lodash/maxBy';
import _sortBy                 from 'lodash/sortBy';
import _get                    from 'lodash/get';
import devToolbarStore         from '@widesk-core/stores/devToolbarStore';
import { Request }             from '@widesk-core/stores/devToolbarStore';
import cacheSystem             from '@widesk-core/models/apiCacheSystem';
import { ApiOutlined }         from '@ant-design/icons';
import { BugOutlined }         from '@ant-design/icons';
import { ClearOutlined }       from '@ant-design/icons';
import { ClockCircleOutlined } from '@ant-design/icons';
import { CloseCircleOutlined } from '@ant-design/icons';
import { CloseOutlined }       from '@ant-design/icons';
import { CodeOutlined }        from '@ant-design/icons';
import { CopyOutlined }        from '@ant-design/icons';
import { DatabaseOutlined }    from '@ant-design/icons';
import { DownOutlined }        from '@ant-design/icons';
import { FieldTimeOutlined }   from '@ant-design/icons';
import { observer }            from 'mobx-react-lite';
import { useLocation }         from 'react-router-dom';
import antdTheme               from 'antd/lib/theme';
import { message }             from '@widesk-ui/hooks/useMessage';

const WARN_SLOW_REQUEST = 500; // Une requête est trop lente (ms)
const WARN_SLOW_REQUESTS = 3000; // Le temps total de chargement des requêtes est trop lent (ms)
const WARN_TO_MANY_REQUESTS = 20; // Il y a trop de requête sur la page

const STORAGE_KEY = 'dev_toolbar_open';
const TAB_STORAGE_KEY = 'dev_toolbar_tab';

type Level = 'ok' | 'warning' | 'error';

type TabKey = 'requests' | 'cached' | 'errors' | 'slow' | 'duplicates' | 'console';

type Timeline = {
	start: number;
	span: number;
};

type RequestRow = {
	request: Request;
	count?: number;
};

const requestUrl = (r: Request): string => {
	return r.title || r.response?.request?.responseURL || r.response?.config?.url || '';
};

const requestStatus = (r: Request): number | undefined => {
	return _get(r, 'response.request.status') || r.response?.status;
};

const isErrorRequest = (r: Request) => !r.response?.status;

const formatDuration = (ms: number) => ms >= 1000 ? `${(ms / 1000).toFixed(1).replace('.', ',')} s` : `${ms} ms`;

const DevToolbar = devToolbarStore.enabled ? observer(() => {
	const [visible, setVisible] = React.useState(localStorage.getItem(STORAGE_KEY) === 'true');
	const [tab, setTab] = React.useState<TabKey | null>(localStorage.getItem(TAB_STORAGE_KEY) as TabKey | null);
	const [cacheEnabled, setCacheEnabled] = React.useState(cacheSystem.enabled);

	const location = useLocation();
	const { token: theme } = antdTheme.useToken();

	React.useEffect(() => devToolbarStore.clearRequests(), [location]);

	React.useEffect(() => localStorage.setItem(STORAGE_KEY, visible + ''), [visible]);

	React.useEffect(() => tab ? localStorage.setItem(TAB_STORAGE_KEY, tab) : localStorage.removeItem(TAB_STORAGE_KEY), [tab]);

	React.useEffect(() => cacheSystem.disable(!cacheEnabled), [cacheEnabled]);

	React.useEffect(() => {
		if (!tab) return;

		const onKeyDown = (e: KeyboardEvent) => e.key === 'Escape' && setTab(null);
		window.addEventListener('keydown', onKeyDown);

		return () => window.removeEventListener('keydown', onKeyDown);
	}, [tab]);

	const requests = devToolbarStore.requests.filter(r => r.duration);
	const cachedRequests = devToolbarStore.requests.filter(r => !r.duration);
	const consoleErrors = devToolbarStore.consoleErrors;

	const requestErrors = requests.filter(isErrorRequest);
	const slowRequests = _sortBy(requests.filter(r => (r.duration || 0) > WARN_SLOW_REQUEST), r => r.duration).reverse();
	const slowestRequest = _maxBy(requests, r => r.duration);
	const firstStartRequest = _minBy(requests, r => r.startTime);
	const lastEndRequest = _maxBy(requests, r => r.endTime);
	const totalDuration = ((lastEndRequest?.endTime || 0) as number) - ((firstStartRequest?.startTime || 0) as number);

	const duplicateds = Object.values(_groupBy(requests, r => `${r.response?.config?.method} ${requestUrl(r)}`))
		.filter(values => values.length > 1)
		.map(values => ({ request: values[0] as Request, count: values.length }));

	const colors: Record<Level, string | undefined> = {
		ok: undefined,
		warning: theme.colorWarning,
		error: theme.colorError,
	};

	const levels = {
		requests: requests.length > WARN_TO_MANY_REQUESTS ? 'warning' : 'ok',
		errors: requestErrors.length ? 'error' : 'ok',
		slow: slowRequests.length ? 'warning' : 'ok',
		total: totalDuration > WARN_SLOW_REQUESTS ? 'warning' : 'ok',
		duplicates: duplicateds.length ? 'warning' : 'ok',
		console: consoleErrors.length ? 'error' : 'ok',
	} satisfies Record<string, Level>;

	const worstLevel: Level = Object.values(levels).includes('error')
		? 'error'
		: Object.values(levels).includes('warning') ? 'warning' : 'ok';

	if (!visible) {
		return (
			<Tooltip title="Ouvrir la barre de debug">
				<button
					className="widesk-dev-toolbar__opener"
					onClick={() => setVisible(true)}
					style={{
						background: 'var(--nav-bg)',
						color: theme.colorTextLightSolid,
						borderColor: theme.colorBorderSecondary,
						borderRadius: `${theme.borderRadius}px ${theme.borderRadius}px 0 0`,
					}}
				>
					<BugOutlined />

					{worstLevel !== 'ok' && (
						<span className="widesk-dev-toolbar__dot" style={{ background: colors[worstLevel] }} />
					)}
				</button>
			</Tooltip>
		);
	}

	const toggleTab = (key: TabKey) => setTab(tab === key ? null : key);

	const tabs: { key: TabKey; label: string; count: number; }[] = [
		{ key: 'requests', label: 'Requêtes', count: requests.length },
		{ key: 'cached', label: 'En cache', count: cachedRequests.length },
		{ key: 'errors', label: 'Erreurs', count: requestErrors.length },
		{ key: 'slow', label: 'Lentes', count: slowRequests.length },
		{ key: 'duplicates', label: 'Doublons', count: duplicateds.length },
		{ key: 'console', label: 'Console', count: consoleErrors.length },
	];

	const rowsByTab: Record<Exclude<TabKey, 'console'>, RequestRow[]> = {
		requests: _sortBy(requests, r => r.startTime).map(request => ({ request })),
		cached: cachedRequests.map(request => ({ request })),
		errors: requestErrors.map(request => ({ request })),
		slow: slowRequests.map(request => ({ request })),
		duplicates: duplicateds,
	};

	return (
		<View className="widesk-dev-toolbar" rel color={theme.colorTextLightSolid} style={{ background: 'var(--nav-bg)' }}>
			{!!tab && (
				<DevToolbarPanel
					tab={tab}
					tabs={tabs}
					onTabChange={setTab}
					onClose={() => setTab(null)}
					rows={tab === 'console' ? undefined : rowsByTab[tab]}
					consoleErrors={consoleErrors}
					timeline={{ start: +(firstStartRequest?.startTime || 0), span: totalDuration }}
				/>
			)}

			<View row centerV style={{ minHeight: 36, borderTop: `1px solid ${theme.colorBorderSecondary}` }}>
				<View row centerV flex className="widesk-dev-toolbar__metrics">
					<Metric
						icon={<ApiOutlined />}
						value={requests.length}
						label={requests.length > 1 ? 'requêtes' : 'requête'}
						hint={`Alerte au-delà de ${WARN_TO_MANY_REQUESTS} requêtes`}
						color={colors[levels.requests]}
						isActive={tab === 'requests'}
						onClick={() => toggleTab('requests')}
					/>

					<Metric
						icon={<DatabaseOutlined />}
						value={cachedRequests.length}
						label="en cache"
						hint="Réponses servies par le cache local"
						isActive={tab === 'cached'}
						onClick={() => toggleTab('cached')}
					/>

					<Metric
						icon={<CloseCircleOutlined />}
						value={requestErrors.length}
						label={requestErrors.length > 1 ? 'erreurs' : 'erreur'}
						color={colors[levels.errors]}
						isActive={tab === 'errors'}
						onClick={() => toggleTab('errors')}
					/>

					<Metric
						icon={<ClockCircleOutlined />}
						value={formatDuration(slowestRequest?.duration || 0)}
						label="la plus lente"
						hint={`Alerte au-delà de ${WARN_SLOW_REQUEST} ms par requête`}
						color={colors[levels.slow]}
						isActive={tab === 'slow'}
						onClick={() => toggleTab('slow')}
					/>

					<Metric
						icon={<FieldTimeOutlined />}
						value={formatDuration(totalDuration)}
						label="au total"
						hint={`Du début de la première requête à la fin de la dernière. Alerte au-delà de ${WARN_SLOW_REQUESTS} ms`}
						color={colors[levels.total]}
						onClick={() => toggleTab('requests')}
					/>

					<Metric
						icon={<CopyOutlined />}
						value={duplicateds.length}
						label={duplicateds.length > 1 ? 'doublons' : 'doublon'}
						hint="Même méthode et même URL appelées plusieurs fois"
						color={colors[levels.duplicates]}
						isActive={tab === 'duplicates'}
						onClick={() => toggleTab('duplicates')}
					/>

					<Metric
						icon={<CodeOutlined />}
						value={consoleErrors.length}
						label="console"
						hint="Erreurs écrites dans la console"
						color={colors[levels.console]}
						isActive={tab === 'console'}
						onClick={() => toggleTab('console')}
					/>
				</View>

				<View row centerV className="widesk-dev-toolbar__actions">
					<Tooltip title={(cacheEnabled ? 'Désactiver' : 'Activer') + ' le cache des requêtes'}>
						<button
							className={`widesk-dev-toolbar__action${cacheEnabled ? '' : ' widesk-dev-toolbar__action--off'}`}
							onClick={() => setCacheEnabled(!cacheEnabled)}
						>
							<DatabaseOutlined />
						</button>
					</Tooltip>

					<Tooltip title="Vider les compteurs et le cache">
						<button
							className="widesk-dev-toolbar__action"
							onClick={() => {
								cacheSystem.clear();
								devToolbarStore.clear();
							}}
						>
							<ClearOutlined />
						</button>
					</Tooltip>

					<Tooltip title="Réduire">
						<button className="widesk-dev-toolbar__action" onClick={() => setVisible(false)}>
							<DownOutlined />
						</button>
					</Tooltip>
				</View>
			</View>
		</View>
	);
}) : () => null;

const Metric = (props: {
	icon: React.ReactNode;
	value: React.ReactNode;
	label: string;
	hint?: string;
	color?: string;
	isActive?: boolean;
	onClick: () => void;
}) => {
	return (
		<Tooltip title={props.hint}>
			<button
				className={`widesk-dev-toolbar__metric${props.isActive ? ' widesk-dev-toolbar__metric--active' : ''}`}
				onClick={props.onClick}
				style={{ color: props.color }}
			>
				{props.icon}

				<span className="widesk-dev-toolbar__value">{props.value}</span>

				<span className="widesk-dev-toolbar__label">{props.label}</span>
			</button>
		</Tooltip>
	);
};

const DevToolbarPanel = (props: {
	tab: TabKey;
	tabs: { key: TabKey; label: string; count: number; }[];
	onTabChange: (tab: TabKey) => void;
	onClose: () => void;
	rows?: RequestRow[];
	consoleErrors: string[];
	timeline: Timeline;
}) => {
	const { token: theme } = antdTheme.useToken();
	const [search, setSearch] = React.useState('');

	const query = search.trim().toLowerCase();

	const rows = (props.rows || []).filter(row => !query || requestUrl(row.request).toLowerCase().includes(query));
	const consoleErrors = props.consoleErrors.filter(err => !query || err.toLowerCase().includes(query));

	const isEmpty = props.rows ? !rows.length : !consoleErrors.length;

	return (
		<View
			className="widesk-dev-toolbar__panel"
			bg={theme.colorBgElevated}
			color={theme.colorText}
			style={{ borderColor: theme.colorBorderSecondary, boxShadow: theme.boxShadowSecondary }}
		>
			<View row centerV gap="xs" className="widesk-dev-toolbar__panel-head" style={{ borderColor: theme.colorBorderSecondary }}>
				<View row centerV flex className="widesk-dev-toolbar__tabs">
					{props.tabs.map(tab => (
						<button
							key={tab.key}
							className={`widesk-dev-toolbar__tab${props.tab === tab.key ? ' widesk-dev-toolbar__tab--active' : ''}`}
							onClick={() => props.onTabChange(tab.key)}
							style={props.tab === tab.key ? { color: theme.colorPrimary, borderColor: theme.colorPrimary } : undefined}
						>
							{tab.label}
							<span className="widesk-dev-toolbar__tab-count" style={{ color: theme.colorTextTertiary }}>{tab.count}</span>
						</button>
					))}
				</View>

				<AntdInput
					size="small"
					allowClear
					placeholder="Filtrer"
					value={search}
					onChange={e => setSearch(e.target.value)}
					className="widesk-dev-toolbar__search"
				/>

				<Button type="text" size="small" icon={<CloseOutlined />} onClick={props.onClose} title="Fermer (Échap)" />
			</View>

			<View className="widesk-dev-toolbar__panel-body">
				{isEmpty && (
					<View center padding="lg" color={theme.colorTextTertiary}>
						{query ? 'Aucun résultat pour ce filtre.' : 'Rien à signaler.'}
					</View>
				)}

				{!props.rows && consoleErrors.map((err, idx) => (
					<View
						key={idx}
						className="widesk-dev-toolbar__console"
						color={theme.colorError}
						style={{ borderColor: theme.colorBorderSecondary, fontFamily: theme.fontFamilyCode }}
					>
						{err}
					</View>
				))}

				{rows.map((row, idx) => (
					<RequestLine key={idx} row={row} timeline={props.timeline} />
				))}
			</View>
		</View>
	);
};

const RequestLine = ({ row, timeline }: { row: RequestRow; timeline: Timeline; }) => {
	const { token: theme } = antdTheme.useToken();

	const r = row.request;
	const url = requestUrl(r);
	const status = requestStatus(r);
	const method = r.response?.config?.method?.toUpperCase();
	const errorMessage = isErrorRequest(r) ? _get(r, 'response.message') : undefined;

	const statusColor = !status ? theme.colorError
		: status >= 500 ? theme.colorError
			: status >= 400 ? theme.colorWarning
				: theme.colorSuccess;

	const durationColor = (r.duration || 0) > WARN_SLOW_REQUEST ? theme.colorWarning : theme.colorTextSecondary;

	let path = url;
	let search = '';

	try {
		const parsed = new URL(url, window.location.origin);
		path = parsed.pathname;
		search = decodeURIComponent(parsed.search);
	} catch {
		// Une clé de cache n'est pas toujours une URL : on l'affiche telle quelle.
	}

	const copy = async () => {
		await navigator.clipboard.writeText(url);
		message.success('URL copiée');
	};

	return (
		<View className="widesk-dev-toolbar__request" style={{ borderColor: theme.colorBorderSecondary }} onClick={copy}>
			<View row centerV gap="xs">
				<span className="widesk-dev-toolbar__method" style={{ color: theme.colorTextSecondary }}>{method || '—'}</span>

				{status !== undefined && (
					<span className="widesk-dev-toolbar__status" style={{ color: statusColor }}>{status || 'ERR'}</span>
				)}

				<span className="widesk-dev-toolbar__url" title={url} style={{ fontFamily: theme.fontFamilyCode }}>
					{path}
					<span style={{ color: theme.colorTextTertiary }}>{search}</span>
				</span>

				{!!row.count && (
					<span className="widesk-dev-toolbar__count" style={{ background: theme.colorWarningBg, color: theme.colorWarningText }}>
						×{row.count}
					</span>
				)}

				{!!r.duration && (
					<span className="widesk-dev-toolbar__duration" style={{ color: durationColor }}>
						{formatDuration(r.duration)}
					</span>
				)}
			</View>

			{!!r.duration && !!r.startTime && timeline.span > 0 && (
				<View className="widesk-dev-toolbar__bar" bg={theme.colorFillTertiary}>
					<View
						heightF
						bg={(r.duration || 0) > WARN_SLOW_REQUEST ? theme.colorWarning : theme.colorPrimary}
						style={{
							marginLeft: `${((+r.startTime - timeline.start) / timeline.span) * 100}%`,
							width: `${Math.max((r.duration / timeline.span) * 100, .5)}%`,
						}}
					/>
				</View>
			)}

			{!!errorMessage && <View color={theme.colorError} size={12}>{errorMessage}</View>}
		</View>
	);
};

export default DevToolbar;
